import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './MasterSidebar.css';
import channelsIcon from '../images/icons/channels-icon.svg';
import uploaderIcon from '../images/icons/uploader-dropbox-icon.svg';
import historyIcon from '../images/icons/history-icon.svg';
import talentRequestIcon from '../images/icons/talent-request-icon.svg';
import inProductionIcon from '../images/icons/in-production-icon.svg';
import messagingScheduleIcon from '../images/icons/messaging-schedule-icon.svg';
import accountIcon from '../images/icons/account-icon.svg';
import usersIcon from '../images/icons/users-icon.svg';
import locationsIcon from '../images/icons/locations-icon.svg';
import billingIcon from '../images/icons/billing-icon.svg';
import supportIcon from '../images/icons/support-icon.svg';
import faqIcon from '../images/icons/faq-icon.svg';
import { useBrand } from '../context/BrandContext';
import { useTenant } from '../tenant/TenantContext';
import { getSidebarVisibility } from '../tenant/sidebarVisibility';
import SidebarReleaseFlipCard from './SidebarReleaseFlipCard';
import SidebarExpandableNavLabel from './SidebarExpandableNavLabel';
import { resolveActiveBrandChrome } from '../activeBrandChrome';
import {
  BRAND_SUPPORT_PATH,
  CLIENT_SUPPORT_HUB_PATH,
  isBrandSupportPath,
  isClientSupportHubPath,
} from '../supportHubPaths';

const MasterSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearBrand, currentBrand } = useBrand();
  const { tenant } = useTenant();
  const vis = getSidebarVisibility(tenant?.kind || 'client');
  const activeBrandChrome = resolveActiveBrandChrome(currentBrand);
  const [isAccountExpanded, setIsAccountExpanded] = useState(false);
  const [isAccountActive, setIsAccountActive] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);

  const handleAccountClick = () => {
    setIsAccountExpanded(!isAccountExpanded);
    setIsAccountActive(true);
    const navigationState = {
      brandName: location.state?.brandName,
      brandType: location.state?.brandType,
      brandId: location.state?.brandId
    };
    navigate('/master-account/account', { state: navigationState });
  };

  const handleSubItemClick = (path) => {
    navigate(path, { 
      state: {
        brandName: location.state?.brandName,
        brandType: location.state?.brandType,
        brandId: location.state?.brandId
      }
    });
    setIsAccountActive(true);
    setIsAccountExpanded(true);
  };

  const handleMenuItemClick = (path) => {
    navigate(path, { 
      state: {
        brandName: location.state?.brandName,
        brandType: location.state?.brandType,
        brandId: location.state?.brandId
      }
    });
    setIsAccountActive(false);
    setIsAccountExpanded(false);
    setExpandedSubmenu(null);
  };

  const toggleSubmenu = (submenu) => {
    if (submenu === 'userManagement') {
      setIsAccountActive(false);
      setIsAccountExpanded(false);
    }
    
    setExpandedSubmenu(expandedSubmenu === submenu ? null : submenu);
  };

  const showMusicHeading =
    vis.musicSection && (vis.channels || vis.uploader || vis.history);
  const showMessagesHeading =
    vis.messagesSection &&
    (vis.talentRequest || vis.inProduction || vis.messagingSchedule);
  const showManageHeading =
    vis.manageSection &&
    (vis.account ||
      vis.userManagement ||
      vis.locations ||
      vis.billing ||
      vis.support ||
      vis.faq);

  return (
    <div className="sidebar master-sidebar" data-testid="master-sidebar">
      <div className="sidebar-scroll-region">
      {vis.brandManager && (
        <div 
          className={`submenu-item ${location.pathname === '/brand-manager' ? 'active' : ''}`}
          onClick={() => {
            clearBrand();
            navigate('/brand-manager');
          }}
          style={{ marginBottom: '20px', marginTop: '20px' }}
        >
          <img src={accountIcon} alt="" className="submenu-icon" />
          Brand Manager
        </div>
      )}
      {showMusicHeading && <div className="sidebar-heading">Music</div>}
      {vis.channels && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/channels' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/channels')}
        >
          <img src={channelsIcon} alt="" className="submenu-icon" />
          Channels
        </div>
      )}
      {vis.uploader && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/musicuploader' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/musicuploader')}
        >
          <img src={uploaderIcon} alt="" className="submenu-icon" />
          Uploader
        </div>
      )}
      {vis.history && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/musichistory' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/musichistory')}
        >
          <img src={historyIcon} alt="" className="submenu-icon" />
          History
        </div>
      )}
      
      {showMessagesHeading && <div className="sidebar-heading">Messages</div>}
      {vis.talentRequest && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/talent-request' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/talent-request')}
        >
          <img src={talentRequestIcon} alt="" className="submenu-icon" />
          Talent Request
        </div>
      )}
      {vis.inProduction && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/in-production' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/in-production')}
        >
          <img src={inProductionIcon} alt="" className="submenu-icon" />
          In Production
        </div>
      )}
      {vis.messagingSchedule && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/messaging-schedule' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/messaging-schedule')}
        >
          <img src={messagingScheduleIcon} alt="" className="submenu-icon" />
          Messaging Schedule
        </div>
      )}
      
      {showManageHeading && <div className="sidebar-heading">Manage</div>}
      {vis.account && (
        <>
          <div 
            className={`submenu-item account-item ${isAccountActive && !location.pathname.includes('/master-account/user-management') ? 'active' : ''}`}
            onClick={() => {
              handleAccountClick();
              setExpandedSubmenu(null);
            }}
          >
            <img src={accountIcon} alt="" className="submenu-icon" />
            <SidebarExpandableNavLabel
              label="Account"
              expanded={isAccountExpanded}
            />
          </div>
          {isAccountExpanded && (
            <div className="account-submenu">
              <div 
                className={`submenu-subitem ${location.pathname === '/master-account/account' && !location.pathname.includes('/security') && !location.pathname.includes('/company') && !location.pathname.includes('/payment') ? 'active' : ''}`}
                onClick={() => handleSubItemClick('/master-account/account')}
              >
                My Profile
              </div>
              <div 
                className={`submenu-subitem ${location.pathname.includes('/master-account/account/security') ? 'active' : ''}`}
                onClick={() => handleSubItemClick('/master-account/account/security')}
              >
                Security
              </div>
              <div 
                className={`submenu-subitem ${location.pathname.includes('/master-account/account/company') ? 'active' : ''}`}
                onClick={() => handleSubItemClick('/master-account/account/company')}
              >
                Company Profile
              </div>
              <div 
                className={`submenu-subitem ${location.pathname.includes('/master-account/account/payment') ? 'active' : ''}`}
                onClick={() => handleSubItemClick('/master-account/account/payment')}
              >
                Payment Method
              </div>
            </div>
          )}
        </>
      )}
      {vis.userManagement && (
        <>
          <div 
            className={`submenu-item ${location.pathname.includes('/master-account/user-management') ? 'active' : ''}`}
            onClick={() => {
              navigate('/master-account/user-management/users');
              toggleSubmenu('userManagement');
              setIsAccountActive(false);
              setIsAccountExpanded(false);
            }}
          >
            <img src={usersIcon} alt="" className="submenu-icon" />
            <SidebarExpandableNavLabel
              label="User Management"
              expanded={expandedSubmenu === 'userManagement'}
            />
          </div>
          {expandedSubmenu === 'userManagement' && (
            <div className="account-submenu">
              <div 
                className={`submenu-subitem ${location.pathname.includes('/master-account/user-management') && !location.pathname.includes('/roles') ? 'active' : ''}`}
                onClick={() => navigate('/master-account/user-management/users')}
              >
                Users
              </div>
              <div 
                className={`submenu-subitem ${location.pathname.includes('/master-account/user-management/roles') ? 'active' : ''}`}
                onClick={() => navigate('/master-account/user-management/roles')}
              >
                Roles
              </div>
            </div>
          )}
        </>
      )}
      {vis.locations && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/locations' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/locations')}
        >
          <img src={locationsIcon} alt="" className="submenu-icon" />
          Locations
        </div>
      )}
      {vis.billing && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/billing' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/billing')}
        >
          <img src={billingIcon} alt="" className="submenu-icon" />
          Billing
        </div>
      )}
      {vis.support && (
        <div 
          className={`submenu-item ${
            isBrandSupportPath(location.pathname) ||
            isClientSupportHubPath(location.pathname)
              ? 'active'
              : ''
          }`}
          // Inside a selected Brand, Support is that Brand's Support
          // (SUPPORT-QA-01). Without a Brand, fall back to the Client hub.
          onClick={() =>
            handleMenuItemClick(
              activeBrandChrome?.id ? BRAND_SUPPORT_PATH : CLIENT_SUPPORT_HUB_PATH,
            )
          }
          data-testid="master-sidebar-nav-support"
        >
          <img src={supportIcon} alt="" className="submenu-icon" />
          Support
        </div>
      )}
      {vis.faq && (
        <div 
          className={`submenu-item ${location.pathname === '/master-account/faq' ? 'active' : ''}`}
          onClick={() => handleMenuItemClick('/master-account/faq')}
        >
          <img src={faqIcon} alt="" className="submenu-icon" />
          FAQ
        </div>
      )}
      </div>
      {/* Fixed bottom Brand card — Manage-matching rule above card */}
      <div
        className="sidebar-brand-footer"
        data-testid="sidebar-brand-footer"
      >
        <div
          className="sidebar-brand-footer-rule"
          data-testid="sidebar-brand-footer-rule"
          aria-hidden="true"
        />
        <SidebarReleaseFlipCard
          logoUrl={activeBrandChrome?.logoUrl || null}
          displayName={activeBrandChrome?.name || null}
          brandId={activeBrandChrome?.id || null}
        />
      </div>
    </div>
  );
};

export default MasterSidebar;
